import moment from 'moment';
import * as utils from '../../core/utils';

export const ReclamationType = {
  Claim: 0,
  Rewash: 1,
};

export const ReclamationWashType = {
  Exterior: 0,
  Interior: 1,
  Both: 2,
};

export const PluralReclamationType = {
  claims: 0,
  rewashes: 1,
};

export const ReclamationStatus = {
  Incomplete: 0,
  Open: 1,
  Investigating: 2,
  RepairApproved: 3,
  RepairDenied: 4,
  Completed: 5,
  Closed: 6,
  Resolved: 7,
  FinalApproval: 8,
  All: 10,
  AllOpen: 11,
  AllClosed: 12,
};

// Claim statuses in the order they're offered. Resolved and Denied are the final
// statuses (and can be reopened); Resolved is listed last.
export const CLAIM_STATUS_DISPLAY_ORDER = [
  ReclamationStatus.Incomplete,
  ReclamationStatus.Open,
  ReclamationStatus.Investigating,
  ReclamationStatus.RepairApproved,
  ReclamationStatus.FinalApproval,
  ReclamationStatus.RepairDenied,
  ReclamationStatus.Resolved,
];

// No longer offered. Claims still in these statuses display them until they are
// migrated to Resolved.
export const LEGACY_CLAIM_STATUSES = [ReclamationStatus.Completed, ReclamationStatus.Closed];

export const CLOSED_RECLAMATION_STATUSES = [
  ReclamationStatus.RepairDenied,
  ReclamationStatus.Resolved,
  ReclamationStatus.Completed,
  ReclamationStatus.Closed,
];

export const ACTIVE_RECLAMATION_STATUSES = [
  ...CLAIM_STATUS_DISPLAY_ORDER,
  ...LEGACY_CLAIM_STATUSES,
].filter((status) => !CLOSED_RECLAMATION_STATUSES.includes(status));

// Statuses that end the claim's working life, and so are the point at which it is
// worth asking for completion photos or videos.
export const CLAIM_COMPLETION_FILE_STATUSES = [
  ReclamationStatus.Resolved,
  ReclamationStatus.Completed,
  ReclamationStatus.Closed,
];

const REOPEN_STATUSES = [ReclamationStatus.Open, ReclamationStatus.Investigating];

export const CLAIM_STATUS_TRANSITIONS = {
  [ReclamationStatus.Incomplete]: [ReclamationStatus.Open],
  [ReclamationStatus.Open]: [
    ReclamationStatus.Investigating,
    ReclamationStatus.RepairApproved,
    ReclamationStatus.FinalApproval,
    ReclamationStatus.RepairDenied,
    ReclamationStatus.Resolved,
  ],
  [ReclamationStatus.Investigating]: [
    ReclamationStatus.Open,
    ReclamationStatus.RepairApproved,
    ReclamationStatus.FinalApproval,
    ReclamationStatus.RepairDenied,
    ReclamationStatus.Resolved,
  ],
  // Estimates Required moves on to Approved (FinalApproval), Denied or Resolved.
  [ReclamationStatus.RepairApproved]: [
    ReclamationStatus.FinalApproval,
    ReclamationStatus.RepairDenied,
    ReclamationStatus.Resolved,
  ],
  [ReclamationStatus.RepairDenied]: REOPEN_STATUSES,
  [ReclamationStatus.Resolved]: REOPEN_STATUSES,
  [ReclamationStatus.FinalApproval]: [ReclamationStatus.Resolved],
  [ReclamationStatus.Completed]: REOPEN_STATUSES,
  [ReclamationStatus.Closed]: [],
};

export const getValidClaimStatusTransitions = (currentStatus) =>
  CLAIM_STATUS_TRANSITIONS[Number(currentStatus)] || [];

// The old claims page has no status rules: any current status can be chosen.
export const getUnrestrictedClaimStatusOptions = (currentStatus) =>
  CLAIM_STATUS_DISPLAY_ORDER.filter((status) => status !== Number(currentStatus));

// Open and Investigating claims reach Approved through the Estimates Required dialog's
// "already have an approved estimate" option, so it isn't a menu item of its own there.
export const getClaimStatusMenuOptions = (currentStatus) =>
  getValidClaimStatusTransitions(currentStatus).filter(
    (status) =>
      status !== ReclamationStatus.FinalApproval ||
      Number(currentStatus) === ReclamationStatus.RepairApproved
  );

// The estimates required email asks the guest for this many estimates unless changed.
export const DEFAULT_ESTIMATES_COUNT = 3;

// Estimates Required, Approved and Denied unlock this many hours after the claim was created.
export const CLAIM_DECISION_LOCK_HOURS = 24;

export const CLAIM_DECISION_STATUSES = [
  ReclamationStatus.RepairApproved,
  ReclamationStatus.FinalApproval,
  ReclamationStatus.RepairDenied,
];

export const isClaimDecisionLocked = (createdAt, now = moment()) =>
  Boolean(createdAt) &&
  moment(now).isBefore(moment(createdAt).add(CLAIM_DECISION_LOCK_HOURS, 'hours'));

export const ReclamationClaimType = {
  StationaryAntenna: 0,
  PowerAntenna: 1,
  Wiper: 2,
  Mirror: 3,
  TrimOrMolding: 4,
  Scratch: 5,
  Bumper: 6,
  Windshield: 7,
  Wheel: 8,
  Tire: 9,
  Other: 10,
  All: 11,
};

export const IssueSiteStatus = {
  Pending: 0,
  Accepted: 1,
  Declined: 2,
};

class Reclamation {
  constructor(props = {}) {
    this.id = props.id;
    this.customerName = props.customerName;
    this.customerEmail = props.customerEmail;
    this.customerPhone = props.customerPhone;
    this.type = utils.toEnum(ReclamationType, props.type);
    this.issueWashType = utils.toEnum(ReclamationWashType, props.issueWashType);
    this.status = utils.toEnum(ReclamationStatus, props.status);
    this.description = props.description;
    this.incidentDetails = props.incidentDetails;
    this.vehicleDetails = props.vehicleDetails || {};
    this.cost = props.cost;
    this.currency = props.currency;
    this.rewashed = props.rewashed;
    this.employeeIds = props.employeeIds;
    this.employees = props.employees;
    this.siteId = props.siteId;
    this.site = props.site;
    this.createdAt = props.createdAt;
    this.updatedAt = props.updatedAt;
    this.updated_at = props.updated_at;
    this.modifiedAt = props.modifiedAt;
    this.modified_at = props.modified_at;
    this.issueDate = props.issueDate;
    this.issueSite = props.issueSite;
    this.issueSiteStatus = utils.toEnum(IssueSiteStatus, props.issueSiteStatus);
    this.issueSiteNote = props.issueSiteNote;
    this.files = props.files;
    this.rewashOperation = props.rewashOperation;
    this.notes = props.notes;
    this.repairItems = props.repairItems;
    this.claimDamagePartId = props.claimDamagePartId;
    this.claimDamageAreaId = props.claimDamageAreaId;
    this.repairShopName = props.repairShopName;
    this.repairShopAddress = props.repairShopAddress;
    this.approvedAmount = props.approvedAmount;
    this.approvedRepairs = props.approvedRepairs;
    this.denialReason = props.denialReason;
    this.resolution = props.resolution;
    this.additionalNotes = props.additionalNotes;
    this.archivedAt = props.archivedAt;
    this.repairItems = props.repairItems;
    this.history = props.history;
  }

  static carPartData() {
    return [
      {
        id: 'StationaryAntenna',
        value: 'Stationary Antenna',
        options: [
          { id: 'CrackedBase', value: 'Cracked Base' },
          { id: 'LostAntenna', value: 'Lost Antenna' },
        ],
      },
      {
        id: 'PowerAntenna',
        value: 'Power Antenna',
        options: [
          { id: 'CrackedBase', value: 'Cracked Base' },
          { id: 'LostAntenna', value: 'Lost Antenna' },
        ],
      },
      {
        id: 'Wiper',
        value: 'Wiper',
      },
      {
        id: 'Mirror',
        value: 'Mirror',
        options: [
          { id: 'MirrorGlass', value: 'Mirror Glass' },
          { id: 'MirrorPlastic', value: 'Mirror Plastic' },
        ],
      },
      {
        id: 'TrimOrMolding',
        value: 'Trim / Molding',
        options: [
          { id: 'TrimLost', value: 'Trim Lost' },
          { id: 'TrimDamaged', value: 'Trim Damaged' },
          { id: 'PlasticBurnt', value: 'Plastic Burnt' },
        ],
      },
      {
        id: 'Scratch',
        value: 'Scratch',
        options: [
          { id: 'ClearcoatScratch', value: 'Clearcoat Scratch' },
          { id: 'BuffableScratch', value: 'Buffable Scratch' },
        ],
      },
      {
        id: 'Bumper',
        value: 'Bumper',
      },
      {
        id: 'Windshield',
        value: 'Windshield',
        options: [{ id: 'WindshieldCrack', value: 'Windshield Crack' }],
      },
      {
        id: 'Wheel',
        value: 'Wheel',
        options: [
          { id: 'WheelScratched', value: 'Wheel Scratched' },
          { id: 'WheelCoatingPeeled', value: 'Wheel Coating Peeled' },
          { id: 'WheelsBurnt', value: 'Wheels Burnt' },
        ],
      },
      {
        id: 'Tire',
        value: 'Tire',
        options: [{ id: 'TirePopped', value: 'Tire Popped' }],
      },
      {
        id: 'Other',
        value: 'Other',
      },
    ];
  }

  static locationInWashData() {
    return [
      { id: 'EntranceOfWash', value: 'Entrance of Wash' },
      { id: 'PresoakArea', value: 'Presoak Area' },
      { id: 'ClothModule1', value: 'Cloth Module 1' },
      { id: 'ClothModule2', value: 'Cloth Module 2' },
      { id: 'ClothModule3', value: 'Cloth Module 3' },
      { id: 'ClothModule4', value: 'Cloth Module 4' },
      { id: 'Wrap1DS', value: 'Wrap 1 DS' },
      { id: 'Wrap1PS', value: 'Wrap 1 PS' },
      { id: 'Wrap2DS', value: 'Wrap 2 DS' },
      { id: 'Wrap2PS', value: 'Wrap 2 PS' },
      { id: 'SideBrushes', value: 'Side Brushes' },
      { id: 'MitterCurtain', value: 'Mitter Curtain' },
      { id: 'HighPressureArch1', value: 'High Pressure Arch 1' },
      { id: 'HighPressureArch2', value: 'High Pressure Arch 2' },
      { id: 'RinseArea', value: 'Rinse Area' },
      { id: 'BlowerRoom', value: 'Blower Room' },
      { id: 'InteriorCleaning', value: 'Interior Cleaning' },
    ];
  }
}

export default Reclamation;
